import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/AppLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CloudUpload, Loader2, CheckCircle2, XCircle, AlertTriangle, Unplug, Folder, FolderOpen, HardDrive, ChevronRight, ArrowLeft } from "lucide-react";

const fmtWhen = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
  } catch { return "—"; }
};
const fmtMB = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.round((b || 0) / 1024)} KB`);

const StatusChip = ({ s }) => {
  const map = {
    success: ["bg-emerald-50 text-emerald-700 border-emerald-200", CheckCircle2, "Success"],
    partial: ["bg-amber-50 text-amber-700 border-amber-200", AlertTriangle, "Partial"],
    failed: ["bg-rose-50 text-rose-700 border-rose-200", XCircle, "Failed"],
    running: ["bg-sky-50 text-sky-700 border-sky-200", Loader2, "Running"],
  };
  const [cls, Icon, label] = map[s] || map.running;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold border rounded-sm px-1.5 py-0.5 ${cls}`}>
      <Icon size={11} className={s === "running" ? "animate-spin" : ""} /> {label}
    </span>
  );
};

const FolderPickerDialog = ({ open, onClose, onPicked }) => {
  const [stack, setStack] = useState([]); // [{id, name}]
  const parent = stack.length ? stack[stack.length - 1].id : "";
  const { data, isLoading } = useQuery({
    queryKey: ["drive-browse", parent],
    queryFn: async () => (await api.get("/backup/drive/browse", { params: { parent } })).data.folders,
    enabled: open,
  });
  const [saving, setSaving] = useState(false);
  const pathLabel = stack.map((s) => s.name).join(" / ");
  const pick = async () => {
    const cur = stack[stack.length - 1];
    setSaving(true);
    try {
      await api.post("/backup/folder", { folder_id: cur.id, folder_name: cur.name, folder_path: pathLabel });
      toast.success(`Backups will be saved in "${cur.name}"`);
      onPicked();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not set folder");
    } finally { setSaving(false); }
  };
  const iconFor = (k) => (k === "shared_drive" ? <HardDrive size={14} className="text-indigo-600" /> : k === "my_drive" ? <HardDrive size={14} className="text-slate-500" /> : <Folder size={14} className="text-amber-500" />);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="folder-picker-dialog">
        <DialogHeader><DialogTitle>Choose backup folder</DialogTitle></DialogHeader>
        <div className="flex items-center gap-1.5 text-xs text-slate-500 min-h-[20px] flex-wrap">
          {stack.length > 0 && (
            <button type="button" onClick={() => setStack(stack.slice(0, -1))} className="inline-flex items-center gap-1 text-cyan-700 hover:underline" data-testid="folder-picker-back">
              <ArrowLeft size={12} /> Back
            </button>
          )}
          <span className="truncate">{pathLabel || "Pick a drive to start"}</span>
        </div>
        <div className="border border-slate-200 rounded-sm max-h-72 overflow-y-auto divide-y divide-slate-100" data-testid="folder-picker-list">
          {isLoading && <div className="py-8 text-center"><Loader2 size={16} className="animate-spin inline text-slate-400" /></div>}
          {!isLoading && (data || []).map((f) => (
            <button key={f.id} type="button" onClick={() => setStack([...stack, { id: f.id, name: f.name }])}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-slate-50" data-testid={`folder-item-${f.id}`}>
              {iconFor(f.kind)} <span className="flex-1 truncate">{f.name}</span> <ChevronRight size={13} className="text-slate-300" />
            </button>
          ))}
          {!isLoading && (data || []).length === 0 && (
            <div className="py-6 text-center text-xs text-slate-400">No sub-folders here</div>
          )}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={pick} disabled={stack.length === 0 || saving} data-testid="folder-picker-select-btn">
            {saving ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <FolderOpen size={14} className="mr-1.5" />}
            Use {stack.length ? `"${stack[stack.length - 1].name}"` : "this folder"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function Backups() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [pickerOpen, setPickerOpen] = useState(false);
  const { data: st, isLoading } = useQuery({
    queryKey: ["backup-status"],
    queryFn: async () => (await api.get("/backup/status")).data,
    refetchInterval: (q) => (q.state.data?.running ? 4000 : 30000),
  });

  useEffect(() => {
    if (params.get("drive") === "connected") {
      toast.success("Google Drive connected — backups are ready to go");
      params.delete("drive");
      setParams(params, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const connect = async () => {
    try {
      const { data } = await api.get("/backup/drive/connect");
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start Google authorisation");
    }
  };
  const disconnect = async () => {
    await api.post("/backup/drive/disconnect");
    toast.success("Google Drive disconnected");
    qc.invalidateQueries({ queryKey: ["backup-status"] });
  };
  const runNow = async () => {
    try {
      await api.post("/backup/run");
      toast.success("Backup started — it will appear in the history below");
      qc.invalidateQueries({ queryKey: ["backup-status"] });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start backup");
    }
  };

  return (
    <div data-testid="backups-page">
      <PageHeader
        title="Backups"
        subtitle="Daily off-site copies of the database and every uploaded file, stored in your own Google Drive."
        breadcrumb={["System", "Backups"]}
      />
      <div className="grid gap-4 lg:grid-cols-3 mt-4">
        <Card className="p-5 lg:col-span-1" data-testid="drive-status">
          <div className="flex items-center gap-2 mb-3">
            <CloudUpload size={18} className="text-cyan-700" />
            <h2 className="text-base font-bold">Google Drive</h2>
          </div>
          {isLoading ? (
            <Loader2 size={16} className="animate-spin text-slate-400" />
          ) : st?.connected ? (
            <>
              <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Connected</Badge>
              <p className="text-sm text-slate-600 mt-2 break-all">{st.account_email}</p>
              {st.needs_reconnect && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2 mt-2" data-testid="backup-reconnect-warning">
                  Folder picking needs a broader Drive permission — click Reconnect and approve again.
                </p>
              )}
              <div className="mt-3 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-sm p-2.5" data-testid="backup-folder-row">
                <div className="flex items-center gap-1.5 font-semibold text-slate-700">
                  <Folder size={13} className="text-amber-500" />
                  {st.folder_name || "CCFI Compass Backups (auto-created in My Drive)"}
                </div>
                {st.folder_path && <div className="text-[10.5px] text-slate-400 mt-0.5 truncate">{st.folder_path}</div>}
                <button type="button" onClick={() => setPickerOpen(true)} className="text-cyan-700 hover:underline mt-1.5 text-xs font-semibold" data-testid="choose-folder-btn">
                  Choose a different folder (incl. shared drives)
                </button>
              </div>
              <p className="text-xs text-slate-400 mt-2">{st.files_indexed} files mirrored · {st.schedule}</p>
              <div className="flex gap-2 mt-4 flex-wrap">
                {st.needs_reconnect && <Button onClick={connect} data-testid="drive-reconnect-btn">Reconnect</Button>}
                <Button onClick={runNow} disabled={st.running} data-testid="backup-run-btn">
                  {st.running ? <><Loader2 size={14} className="animate-spin mr-1.5" /> Backing up…</> : "Back up now"}
                </Button>
                <Button variant="outline" onClick={disconnect} data-testid="drive-disconnect-btn">
                  <Unplug size={14} className="mr-1.5" /> Disconnect
                </Button>
              </div>
            </>
          ) : (
            <>
              <Badge variant="outline" className="text-slate-500">Not connected</Badge>
              <p className="text-sm text-slate-600 mt-2">
                Connect the Google account that should hold the backups. A folder called
                <b> CCFI Compass Backups</b> will be created with a dated database copy each day
                plus a mirror of every uploaded photo and document.
              </p>
              {!st?.credentials_configured && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2 mt-3" data-testid="backup-creds-warning">
                  Google credentials haven’t been added yet — the Connect button will work once they’re configured.
                </p>
              )}
              <Button className="mt-4" onClick={connect} data-testid="drive-connect-btn">
                Connect Google Drive
              </Button>
            </>
          )}
        </Card>

        <Card className="p-5 lg:col-span-2">
          <h2 className="text-base font-bold mb-3">Backup history</h2>
          {(st?.runs || []).length === 0 ? (
            <p className="text-sm text-slate-400 py-8 text-center">No backups yet — connect Drive and run your first one.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="backup-runs-table">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b">
                    <th className="py-2 pr-3">When</th>
                    <th className="py-2 pr-3">Trigger</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Database</th>
                    <th className="py-2 pr-3">Files new</th>
                    <th className="py-2 pr-3">Unchanged</th>
                  </tr>
                </thead>
                <tbody>
                  {st.runs.map((r) => (
                    <tr key={r.id} className="border-b border-slate-100" data-testid={`backup-run-${r.id}`}>
                      <td className="py-2 pr-3 whitespace-nowrap">{fmtWhen(r.started_at)}</td>
                      <td className="py-2 pr-3 capitalize">{r.trigger}</td>
                      <td className="py-2 pr-3">
                        <StatusChip s={r.status} />
                        {r.error && <div className="text-[11px] text-rose-600 mt-0.5 max-w-[260px] truncate" title={r.error}>{r.error}</div>}
                      </td>
                      <td className="py-2 pr-3">{r.db_bytes ? fmtMB(r.db_bytes) : "—"}</td>
                      <td className="py-2 pr-3">{r.files_uploaded ?? "—"}{r.files_failed ? <span className="text-rose-600"> ({r.files_failed} failed)</span> : null}</td>
                      <td className="py-2 pr-3">{r.files_skipped ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
      <FolderPickerDialog open={pickerOpen} onClose={() => setPickerOpen(false)}
        onPicked={() => { setPickerOpen(false); qc.invalidateQueries({ queryKey: ["backup-status"] }); }} />
    </div>
  );
}
